<script setup>
import CFNavbarItem from '@/shared/components/CFNavbarItem.vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { computed } from 'vue';
import CFMigrationSidebarAlert from '@/shared/components/CFMigrationSidebarAlert.vue';
import { usePermissionsStore } from '#modules/auth/stores/permissions.store.js';
import { useAccountStatus } from '@/shared/composables/useAccountStatus.js';
import { useTeamStore } from '#modules/team/stores/team.store.js';
import CFSidebarCollapseButton from '@/shared/components/CFSidebarCollapseButton.vue';
import { useAppStore } from '@/app/stores/app.store.js';
import { storeToRefs } from 'pinia';
import CFQuickAddPopover from '@/shared/components/CFQuickAddPopover.vue';
import CFIcon from '@/ui/Icon/CFIcon.vue';
import { useAuthStore } from '#modules/auth/stores/auth.store.js';
import { AppPermissions } from '#modules/auth/constants/permissions.js';

defineOptions({ inheritAttrs: false });

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();
const teamStore = useTeamStore();
const { hasAllPermissions } = usePermissionsStore();
const { hasFullAccess } = useAccountStatus();

const appStore = useAppStore();
const { sidebarOpen, sidebarExpanded } = storeToRefs(appStore);

const navigation = computed(() => [
    {
        name: t('general.dashboard'),
        href: router.resolve({ name: 'dashboard' }).href,
        icon: 'layout-dashboard',
        guarded: true,
    },
    {
        name: t('general.domain_hosting'),
        href: router.resolve({ name: 'domains' }).href,
        icon: 'network-star',
        permissions: [AppPermissions.ManageDomains],
        guarded: true,
    },
    {
        name: t('general.zones'),
        href: router.resolve({ name: 'zones' }).href,
        icon: 'app-window-cloud',
        permissions: [AppPermissions.ManageZones],
        guarded: true,
    },
    {
        name: t('general.ssl'),
        href: router.resolve({ name: 'ssl' }).href,
        icon: 'app-window-lock',
        permissions: [AppPermissions.ManageSslCertificates],
        guarded: true,
    },
    {
        name: t('general.core'),
        href: router.resolve({ name: 'core' }).href,
        icon: 'server-exchange',
        guarded: true,
        newTab: true,
    },
    {
        name: t('general.sites'),
        href: router.resolve({ name: 'sites.index' }).href,
        icon: 'hierarchy5',
        permissions: [AppPermissions.ManageWebOps],
        guarded: true,
    },
    {
        name: t('redirects.redirects'),
        href: router.resolve({ name: 'redirects' }).href,
        icon: 'strategy-split',
        permissions: [AppPermissions.ManageRedirects],
        guarded: true,
    },
    {
        name: t('general.mail_domains'),
        href: router.resolve({ name: 'mail domains' }).href,
        icon: 'envelope-back-front',
        permissions: [AppPermissions.ManageMail],
        guarded: true,
    },
    {
        name: t('general.contacts'),
        href: router.resolve({ name: 'contacts' }).href,
        icon: 'book-address',
        permissions: [AppPermissions.ManageContacts],
        guarded: true,
    },
    {
        name: t('general.team'),
        href: router.resolve({ name: 'team' }).href,
        icon: 'business-team-goal',
    },
    {
        name: t('general.support'),
        href: router.resolve({ name: 'support' }).href,
        icon: 'headphones-customer-support',
        permissions: [AppPermissions.ManageSupportMessages],
        guarded: true,
    },
    {
        name: t('general.invoices'),
        href: router.resolve({ name: 'invoices' }).href,
        icon: 'accounting-invoice',
        permissions: [AppPermissions.ManageFinancial],
        guarded: true,
    },
]);

const filteredNavigation = computed(() =>
    navigation.value.filter((item) => {
        if (item.guarded && !hasFullAccess.value) {
            return false;
        }

        if (!item.permissions || !Array.isArray(item.permissions)) {
            return true;
        }

        return hasAllPermissions(item.permissions);
    })
);

const logout = () => router.push({ name: 'logout' });
</script>

<template>
    <aside
        id="sidebar"
        data-testid="navigation-sidebar"
        class="sidebar w-76"
        :class="[
            sidebarExpanded ? 'expanded md:w-76' : 'closed md:w-24',
            sidebarOpen ? 'transform-none' : '-translate-x-full md:translate-none',
        ]"
        :data-expanded="sidebarExpanded"
    >
        <div class="flex h-full w-full max-w-full min-w-0 flex-col">
            <div class="logo-container">
                <img
                    alt="Cyberfusion logo"
                    src="/Cyberfusion_Logo_White.svg"
                />

                <svg
                    class="letter size-9 fill-current"
                    viewBox="0 0 142.07 154.04"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M121.98,110.12c-6.7,15.88-22.16,27.27-46.21,27.27-37.26,0-56-28.1-56-60.37s18.95-60.37,56-60.37c23.95,0,37.54,9.98,45.8,27.06h20.03C132,18.63,111.75,0,75.77,0,26.85,0,0,35.18,0,77.02s26.85,77.02,75.77,77.02c36.48,0,59.44-20.65,66.3-43.92,0,0-20.09,0-20.09,0Z"
                    />
                </svg>
            </div>

            <div
                v-if="authStore.isVerified"
                class="sidebar-scroll-content"
            >
                <nav class="navigation">
                    <CFNavbarItem
                        v-for="item in filteredNavigation"
                        :key="item.name"
                        :item="item"
                    />
                </nav>

                <CFMigrationSidebarAlert v-if="hasFullAccess && sidebarExpanded" />

                <CFQuickAddPopover />
            </div>

            <div class="relative shrink-0 px-8 pb-4">
                <div class="navbar-profile">
                    <div class="navbar-profile--avatar">
                        <img
                            v-if="authStore.profilePicture"
                            :src="authStore.profilePicture"
                            :alt="$t('settings.profile_picture')"
                            height="36"
                            width="36"
                        />
                        <div
                            v-else
                            class="from-cf-green to-cf-blue inline-flex size-9 items-center justify-center bg-linear-to-tr text-white select-none"
                        >
                            {{ authStore.initials }}
                        </div>
                    </div>
                    <div class="navbar-profile--name">
                        <p
                            :title="authStore.fullName"
                            class="truncate text-xs leading-3.5 font-bold text-white select-none"
                        >
                            {{ authStore.fullName }}
                        </p>
                        <p
                            v-if="teamStore.hasRequiredInformation"
                            :title="teamStore.team.attributes.name"
                            class="truncate text-xs leading-3.5 text-white select-none group-hover:text-gray-200"
                        >
                            {{ teamStore.team.attributes.name }}
                        </p>
                        <div
                            v-else
                            class="flex items-center text-xs font-medium"
                        >
                            <CFIcon
                                name="alert-circle"
                                class="text-warning mr-1 size-5"
                            />
                            <RouterLink
                                :to="{ name: 'complete registration' }"
                                class="text-warning underline"
                            >
                                {{ $t('general.complete_registration') }}
                            </RouterLink>
                        </div>
                    </div>
                    <RouterLink
                        :to="{ name: 'settings' }"
                        data-testid="settings"
                        class="link-sidebar"
                    >
                        <span class="title">{{ $t('general.settings') }}</span>
                        <CFIcon
                            name="cog"
                            size="18"
                        />
                    </RouterLink>
                    <button
                        type="button"
                        data-testid="logout"
                        class="link-sidebar ml-2"
                        @click="logout"
                    >
                        <span class="title">{{ $t('button.logout') }}</span>
                        <CFIcon
                            name="logout2"
                            size="18"
                        />
                    </button>
                </div>
            </div>
        </div>

        <CFSidebarCollapseButton
            :open="sidebarOpen"
            variant="mobile"
            @toggle="sidebarOpen = !sidebarOpen"
        />

        <CFSidebarCollapseButton
            :open="sidebarExpanded"
            variant="desktop"
            @toggle="sidebarExpanded = !sidebarExpanded"
        />
    </aside>

    <Transition name="fade">
        <div
            v-if="sidebarOpen"
            class="fixed inset-0 z-40 bg-black/70 md:hidden"
            @click="sidebarOpen = false"
        ></div>
    </Transition>
</template>

<style scoped>
@reference "@css/app.css";

.link-sidebar {
    @apply relative cursor-pointer opacity-70 transition duration-300 ease-in-out hover:opacity-100;

    .title {
        @apply invisible absolute -bottom-5 left-1/2 mx-auto -translate-x-1/2 text-[8px] uppercase opacity-0 transition duration-300 ease-in-out;
    }

    &:hover {
        span {
            @apply visible opacity-100;
        }
    }
}
</style>
